import { useState } from "react";
import { CircleHelp } from "lucide-react";
import Button from "@imminently/decisively-ui/Button";
import Dialog from "@imminently/decisively-ui/Dialog";
import Tabs from "@imminently/decisively-ui/Tabs";

/** Downloadable single-file example of the card storage API. */
export const HTML_CARD_EXAMPLE_URL = "/skills/investigation-html-card/examples/settings.html";
/** Author instructions for building a card, written to be handed to a coding assistant. */
export const HTML_CARD_INSTRUCTIONS_URL = "/skills/investigation-html-card/SKILL.md";
const usage = `const project = await decisively.state.load("project");
const session = await decisively.state.load("session");
await decisively.state.save("session", { note: "Hello" });
await decisively.state.clear("session");
const { sessionVersion } = await decisively.getContext();`;
const dataUsage = `<meta name="decisively-bridge" content="2">

const session = await decisively.data.get("session");
const values = await decisively.data.get("attributeValues");
const graph = await decisively.data.get("sessionGraph");
const contacts = await decisively.data.get("contacts");
const ruleGraph = await decisively.data.get("ruleGraph");`;
const codeBlock = "overflow-x-auto rounded bg-muted p-3 text-xs";

/** Parent-owned help stays usable even if uploaded code fails or becomes unresponsive. */
export function HtmlCardHelp(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        icon
        size="sm"
        variant="ghost"
        label="HTML card help"
        onClick={() => setOpen(true)}
      >
        <CircleHelp size={16} />
      </Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
      >
        <Dialog.Content className="max-w-2xl">
          <Dialog.Header>
            <Dialog.Title>HTML card help</Dialog.Title>
            <Dialog.Description>Guides for using HTML cards.</Dialog.Description>
            <Dialog.Controls />
          </Dialog.Header>
          {/* Content is capped at 90vh: the tab list stays put and each tab scrolls inside the body. */}
          <Dialog.Body className="flex min-h-0 flex-1 flex-col pb-6">
            <Tabs
              defaultValue="data"
              className="flex min-h-0 flex-1 flex-col"
            >
              <Tabs.List className="mb-2 shrink-0 border-b border-border">
                <Tabs.Trigger value="data">Data</Tabs.Trigger>
                <Tabs.Trigger value="storage">Storage</Tabs.Trigger>
              </Tabs.List>
              <Tabs.Content
                value="data"
                className="min-h-0 flex-1 space-y-4 overflow-y-auto text-sm"
              >
                <p>
                  Add the <code>decisively-bridge</code> meta tag, then request only the session data your card needs.
                  Each request returns a promise.
                </p>
                <pre className={codeBlock}>
                  <code>{dataUsage}</code>
                </pre>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    <code>session</code>: the session record, without its graph. Interactions marked{" "}
                    <code>hasStoredData</code> list only their details; request them with <code>interaction</code> for
                    the full content.
                  </li>
                  <li>
                    <code>attributeValues</code>: each attribute's result. Unset attributes are left out.
                  </li>
                  <li>
                    <code>sessionGraph</code>: the session's results combined with the rule graph, as nodes (with
                    descriptions and results) and the links showing what each result was derived from.
                  </li>
                  <li>
                    <code>contacts</code>: the contact records linked to the session. Only available to users who can
                    view contacts.
                  </li>
                  <li>
                    <code>ruleGraph</code>: the release rule graph.
                  </li>
                  <li>
                    <code>interaction</code>: one interaction by id, with any stored content included. Use{" "}
                    <code>{`decisively.data.get("interaction", { id })`}</code>.
                  </li>
                  <li>
                    <code>events</code>: every event recorded against the session.
                  </li>
                </ul>
                <p>
                  You can also request <code>decisionReport</code>, but avoid it unless you really need it. It is built
                  from the session and rule graph, can be very large, and is generated in the browser each time it is
                  requested. <code>sessionGraph</code> holds the same information with each node stored once.
                </p>
                <p>
                  Each request returns a snapshot, so request again if you need fresher data. Handle each failure on its
                  own so one unavailable kind doesn't break the whole card.
                </p>
              </Tabs.Content>
              <Tabs.Content
                value="storage"
                className="min-h-0 flex-1 space-y-4 overflow-y-auto text-sm"
              >
                <p>Save settings and results for this card.</p>
                <p>
                  <strong>Project state</strong> is shared across sessions. <strong>Session state</strong> is shared by
                  authorized users across every version of the same session.
                </p>
                <p>
                  Upload your HTML and save the investigation before using storage. The card can address only its own
                  data. Read-only users cannot save changes.
                </p>
                <pre className={codeBlock}>
                  <code>{usage}</code>
                </pre>
                <p>
                  Load before saving or clearing. A save replaces the complete value. An empty location returns{" "}
                  <code>null</code>. Handle rejected promises: after a conflict or uncertain save, load again before
                  retrying.
                </p>
                <p>
                  Replacing this card's HTML preserves its data. Your code manages compatibility. Use a{" "}
                  <code>schemaVersion</code> in your JSON, and store your own per-version values if you need them. The
                  viewed <code>sessionVersion</code> is metadata only.
                </p>
                <p>
                  Project values can be up to 256 KiB and session values up to 1 MiB. Requests are limited to protect
                  shared storage. Do not store credentials; no platform AI keys are supplied.
                </p>
              </Tabs.Content>
            </Tabs>
          </Dialog.Body>
        </Dialog.Content>
      </Dialog>
    </>
  );
}
